import { yupResolver } from '@hookform/resolvers/yup';
import { PROPOSAL_AFIRMATIVE } from 'components/proposal_types';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useQueryParams from 'v2/hooks/useQueryParams';
import { Button } from 'react-bootstrap';
import { FieldPath, useForm } from 'react-hook-form';
import StepWizard, {
  StepWizardChildProps,
  StepWizardProps,
} from 'react-step-wizard';
import {
  FEATURE_FLAGS,
  PROVIDER_RESPONSE_PROPOSAL_EMPTY_ERROR,
  PROVIDER_RESPONSE_PROPOSAL_ERROR_CONTACT_INFO,
} from 'utils/constants';
import {
  CheckDescriptionStatus,
  ProposalEntity,
  RootState,
  SearchEntity,
} from 'v2/models';
import { BLACK_LIST_REGEX, isTextValid } from 'v2/utils/formValidator';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { ProposalResponseAccept } from './ProposalResponseToken';
import StepOne from './StepOne';
import StepQuestion from './StepQuestion';
import StepTwo from './StepTwo';
import { useFeatureFlagContext } from 'v2/context/featureFlagContext';
import SubmitButtonTooltip from '../DataRequests/CheckResponse/SubmitButtonTooltip';
import { useSelector } from 'react-redux';

interface ProposalResponseProps {
  proposal: ProposalEntity;
  onReject: () => Promise<void>;
  onAccept: (data: ProposalResponseAccept) => Promise<void>;
  busy: boolean;
}

export type AnswerType = {
  id: number;
  question: string;
  response: string;
  check_seller_response_status?: CheckDescriptionStatus;
  check_seller_response_feedback?: string;
  prompt_history_id?: number;
};

/*
Unfortunatelly for this form we have to do a hack to use yup validation, react-hook-form and the same form pattern
we have been using for other forms because the number of questions is dynamic, so the number of stpes and validations
are also dynamic. For that reason we have to convert each question into a field and don't use field array
because we render each question on its own screen.
*/
export interface ProviderProposalTokenResponseFields {
  [key: string]: string | AnswerType;
}

const responseValidator = {
  name: 'contact_info',
  test: (value: string | undefined) =>
    isTextValid(value || '', [
      BLACK_LIST_REGEX.email,
      BLACK_LIST_REGEX.phoneNumber,
    ]),
  message: PROVIDER_RESPONSE_PROPOSAL_ERROR_CONTACT_INFO,
};

const getResponseSchema = (isCheckSellerResponseEnabled: boolean) =>
  yup.object().shape({
    response: yup
      .string()
      .test(responseValidator)
      .required(PROVIDER_RESPONSE_PROPOSAL_EMPTY_ERROR),
    check_seller_response_status: yup.string().when('response', {
      is: (response: string) => response && isCheckSellerResponseEnabled,
      then: (schema) =>
        schema.required('Please check the response to continue.'),
      otherwise: (schema) => schema.optional(),
    }),
    prompt_history_id: yup.number().optional(),
  });

const ProposalResponse = ({
  proposal,
  onReject,
  onAccept,
  busy,
}: ProposalResponseProps) => {
  const user = useSelector((state: RootState) => state?.user?.user);
  const { flags } = useFeatureFlagContext();
  const isCheckSellerResponseEnabled =
    (flags && flags[FEATURE_FLAGS.CHECK_SELLER_RESPONSE]) || !user;
  const search = proposal.search as SearchEntity;
  const schemaAnswers = useMemo(() => {
    return search.questions.map((item) => {
      return yup.object().shape({
        [`answers_${item.id}`]: yup.object().shape({
          id: yup.number(),
          question: yup.string(),
          response: yup
            .string()
            .test(responseValidator)
            .required(PROVIDER_RESPONSE_PROPOSAL_EMPTY_ERROR),
        }),
      });
    });
  }, [search.id]);

  const queryParams = useQueryParams();
  const [currentStep, setCurrentStep] = useState(1);
  const wizardInstance = useRef<StepWizardChildProps<StepWizardProps>>();
  const wizardCurrentStep = wizardInstance?.current?.currentStep || 1;
  // 2 is magic number to indicate we have 2 steps before the first question
  const totalSteps = 2 + search.questions.length;
  const isLastStep = currentStep === totalSteps;
  const INITIAL_FORM_STATE = useMemo(() => {
    return {
      response: '',
      prompt_history_id: undefined,
      check_seller_response_status: undefined,
      check_seller_response_feedback: undefined,
      ...search.questions.reduce(
        (acc, item) => ({
          ...acc,
          [`answers_${item.id}`]: {
            id: item.id,
            question: item.name,
            response: '',
          },
        }),
        {}
      ),
    };
  }, [search.id]);
  const schemaResponse = getResponseSchema(isCheckSellerResponseEnabled);
  const schemasValidators = [null, schemaResponse, ...schemaAnswers];
  const {
    register,
    getValues,
    trigger,
    watch,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ProviderProposalTokenResponseFields>({
    defaultValues: INITIAL_FORM_STATE,
    mode: 'onChange',
    resolver: yupResolver(mergeSchemas(schemasValidators)),
  });

  const responseByParam = queryParams.get('response') || '';
  const checkResponse = watch('check_seller_response_status');

  const showTooltip = () => {
    if (isCheckSellerResponseEnabled && !checkResponse && !busy)
      return 'You must check your response first before you can proceed to the next step.';
  };

  useEffect(() => {
    if (wizardInstance.current) {
      wizardInstance.current.onStepChange = ({
        activeStep,
      }: {
        previousStep: number;
        activeStep: number;
      }) => {
        setTimeout(() => {
          setCurrentStep(activeStep);
        });
      };
    }
  }, [wizardCurrentStep]);

  useEffect(() => {
    if (responseByParam === 'accept') {
      wizardInstance.current?.nextStep();
    }
  }, []);

  const onSubmit = async (data: ProviderProposalTokenResponseFields) => {
    delete data.check_seller_response_feedback;
    delete data.check_seller_response_status;
    const { response, prompt_history_id, ...answers } = data;
    const answersParsed: ProposalResponseAccept['answers'] = Object.keys(
      answers
    ).map((item) => ({
      question: Number(item.split('_')[1]),
      response: (answers[item] as AnswerType).response,
    }));

    const parsedData: ProposalResponseAccept = {
      action: PROPOSAL_AFIRMATIVE,
      response: response as string,
      answers: answersParsed,
      prompt_history_id: prompt_history_id as unknown as number | undefined,
    };
    await onAccept(parsedData);
  };

  const goNextStep = async () => {
    try {
      await schemasValidators[currentStep - 1]?.validate(getValues(), {
        abortEarly: false,
      });
      wizardInstance.current?.nextStep();
    } catch (error) {
      trigger(getFieldsWithError(error as yup.ValidationError));
    }
  };

  return (
    <form className="modal-content" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <StepWizard
          transitions={{}}
          instance={(instance) =>
            (wizardInstance.current =
              instance as StepWizardChildProps<StepWizardProps>)
          }
          isLazyMount={true}
        >
          {responseByParam !== 'accept' ? (
            <StepOne
              proposal={proposal}
              onReject={onReject}
              onContinueClick={() => wizardInstance.current?.nextStep()}
            />
          ) : (
            <></>
          )}
          <StepTwo
            proposal={proposal}
            register={register}
            errors={errors}
            isCheckSellerResponseEnabled={isCheckSellerResponseEnabled}
            control={control}
          />

          {
            search.questions.map((question) => {
              return (
                <StepQuestion
                  key={question.id}
                  errors={errors}
                  register={register}
                  question={question}
                />
              );
            }) as unknown as ReactElement
          }
        </StepWizard>
        {wizardCurrentStep > 1 ? (
          <div className="d-flex justify-content-between mt-4 pb-4 px-5">
            <Button
              onClick={() => wizardInstance.current?.previousStep()}
              disabled={
                busy ||
                Boolean(wizardCurrentStep === 2 && responseByParam === 'accept')
              }
            >
              Previous
            </Button>
            <SubmitButtonTooltip
              disabled={busy}
              toolTipMessage={showTooltip()}
              goNextStep={goNextStep}
              isDeepLink={true}
              isLastStep={isLastStep}
            />
          </div>
        ) : null}
      </div>
    </form>
  );
};

export default ProposalResponse;

function getFieldsWithError(error: yup.ValidationError) {
  const fieldsWithError = error?.inner.map((item) => item.path);
  return fieldsWithError as
    | FieldPath<ProviderProposalTokenResponseFields>
    | FieldPath<ProviderProposalTokenResponseFields>[];
}

function mergeSchemas(schemas: Array<yup.ObjectSchema<ObjectShape> | null>) {
  const [first, ...rest] = schemas.filter(Boolean) as Array<
    yup.ObjectSchema<ObjectShape>
  >;

  const merged = rest.reduce(
    (mergedSchemas, schema) => mergedSchemas.concat(schema),
    first
  );

  return merged;
}
